/**
 * Base API client for the Student Immunization Portal
 * Provides centralized HTTP client with interceptors, error handling, and retry logic
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse, ApiError } from '../types';
import { AppError, ErrorCode, normalizeError, withRetry } from '../utils/errorHandling';
import { getRequestLanguage } from '../utils/languagePreference';

/**
 * API configuration
 */
/**
 * Base URL for the API Gateway (Kong)
 * Points to Kong API Gateway which routes to microservices:
 * - /api/guardians → auth-service
 * - /api/students → student-service
 * - /api/vaccinations, /api/immunizations, /api/analyze_image → immunization-service
 * - /api/notifications → notification-service
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 minutes - allows for slow uploads and Gemini processing
  headers: {
    'Content-Type': 'application/json',
  },
};

/**
 * Create the axios instance
 */
const apiClient: AxiosInstance = axios.create(API_CONFIG);

/**
 * Token storage key
 */
const AUTH_TOKEN_KEY = 'auth_token';
const SESSION_ID_KEY = 'session_id';

/**
 * Get stored auth token
 */
export const getAuthToken = (): string | null => {
  return sessionStorage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Set auth token
 */
export const setAuthToken = (token: string): void => {
  sessionStorage.setItem(AUTH_TOKEN_KEY, token);
};

/**
 * Clear auth token
 */
export const clearAuthToken = (): void => {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_ID_KEY);
};

/**
 * Get session ID
 */
export const getSessionId = (): string | null => {
  return sessionStorage.getItem(SESSION_ID_KEY);
};

/**
 * Set session ID
 */
export const setSessionId = (sessionId: string): void => {
  sessionStorage.setItem(SESSION_ID_KEY, sessionId);
};

/**
 * Request interceptor - add auth token and session ID
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const sessionId = getSessionId();
    if (sessionId) {
      config.headers['X-Session-ID'] = sessionId;
    }

    // Add language header for i18n (same preference the UI renders in)
    config.headers['Accept-Language'] = getRequestLanguage();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - handle errors
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiError>) => {
    // Handle network errors
    if (!error.response) {
      return Promise.reject(new AppError({
        code: ErrorCode.NETWORK_ERROR,
        message: 'Network error occurred',
        originalError: error,
      }));
    }

    const { status, data } = error.response;

    // Handle authentication errors
    if (status === 401) {
      clearAuthToken();
      // Dispatch event for auth context to handle
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    // Map to AppError
    return Promise.reject(new AppError({
      code: mapStatusToErrorCode(status),
      message: data?.message || error.message,
      userMessage: data?.detail || undefined,
      details: { status, data },
      originalError: error,
    }));
  }
);

/**
 * Map HTTP status to error code
 */
const mapStatusToErrorCode = (status: number): ErrorCode => {
  const statusMap: Record<number, ErrorCode> = {
    400: ErrorCode.VALIDATION_ERROR,
    401: ErrorCode.UNAUTHORIZED,
    403: ErrorCode.UNAUTHORIZED,
    404: ErrorCode.STUDENT_NOT_FOUND,
    408: ErrorCode.TIMEOUT,
    409: ErrorCode.DUPLICATE_RECORD,
    413: ErrorCode.FILE_TOO_LARGE,
    415: ErrorCode.INVALID_FILE_TYPE,
    422: ErrorCode.VALIDATION_ERROR,
    429: ErrorCode.SERVICE_UNAVAILABLE,
    500: ErrorCode.SERVER_ERROR,
    502: ErrorCode.SERVICE_UNAVAILABLE,
    503: ErrorCode.SERVICE_UNAVAILABLE,
    504: ErrorCode.TIMEOUT,
  };
  return statusMap[status] || ErrorCode.UNKNOWN_ERROR;
};

/**
 * Generic GET request
 */
export const get = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  const response = await apiClient.get<ApiResponse<T>>(url, config);
  return response.data;
};

/**
 * Generic POST request
 */
export const post = async <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config);
  return response.data;
};

/**
 * Generic PUT request
 */
export const put = async <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config);
  return response.data;
};

/**
 * Generic PATCH request
 */
export const patch = async <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
  return response.data;
};

/**
 * Generic DELETE request
 */
export const del = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data;
};

/**
 * Upload file with progress tracking
 */
export const uploadFile = async <T>(
  url: string,
  file: File,
  options?: {
    onProgress?: (percentage: number) => void;
    additionalData?: Record<string, string>;
  }
): Promise<ApiResponse<T>> => {
  const formData = new FormData();
  formData.append('file', file);

  if (options?.additionalData) {
    Object.entries(options.additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const response = await apiClient.post<ApiResponse<T>>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (options?.onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        options.onProgress(percentage);
      }
    },
  });

  return response.data;
};

/**
 * Upload one or more files under the `files` field.
 *
 * Multi-page documents (a 3-page vaccination card, a 2-page booklet) must go
 * to the backend in a single request so the model reads a dose table that
 * continues across pages as one series.
 */
export const uploadFiles = async <T>(
  url: string,
  files: File[],
  options?: {
    onProgress?: (percentage: number) => void;
    additionalData?: Record<string, string>;
  }
): Promise<ApiResponse<T>> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  if (options?.additionalData) {
    Object.entries(options.additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const response = await apiClient.post<ApiResponse<T>>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (options?.onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        options.onProgress(percentage);
      }
    },
  });

  return response.data;
};

/**
 * Upload base64 image
 */
export const uploadBase64Image = async <T>(
  url: string,
  base64Data: string,
  options?: {
    filename?: string;
    additionalData?: Record<string, string>;
  }
): Promise<ApiResponse<T>> => {
  const response = await apiClient.post<ApiResponse<T>>(url, {
    image: base64Data,
    filename: options?.filename,
    ...options?.additionalData,
  });

  return response.data;
};

/**
 * Request with automatic retry
 */
export const requestWithRetry = async <T>(
  requestFn: () => Promise<T>,
  options?: {
    maxAttempts?: number;
    delayMs?: number;
  }
): Promise<T> => {
  return withRetry(requestFn, options);
};

/**
 * Health check endpoint
 */
export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get('/health');
    return response.status === 200;
  } catch {
    return false;
  }
};

/**
 * API endpoints constants
 */
/**
 * API endpoints configuration for microservices architecture via Kong API Gateway
 *
 * Routing through Kong:
 * - /api/guardians/* → auth-service (port 8000)
 * - /api/students/* → student-service (port 8001)
 * - /api/vaccinations/* → immunization-service (port 8002)
 * - /api/notifications/* → notification-service (port 8003)
 */
export const API_ENDPOINTS = {
  // Auth (routed to auth-service via /api/guardians)
  AUTH: {
    REQUEST_OTP: '/api/guardians/auth/otp/request',
    VERIFY_OTP: '/api/guardians/auth/otp/verify',
    REQUEST_WHATSAPP_OTP: '/api/guardians/auth/whatsapp/request',
    VERIFY_WHATSAPP_OTP: '/api/guardians/auth/whatsapp/verify',
    REQUEST_EMAIL_OTP: '/api/guardians/auth/email/request',
    VERIFY_EMAIL_OTP: '/api/guardians/auth/email/verify',
    VERIFY_ID: '/api/guardians/auth/id-verify',
    LOGOUT: '/api/guardians/auth/logout',
    SESSION: '/api/guardians/auth/session',
  },
  // Guardian profile (auth-service, /api/guardians)
  GUARDIAN: {
    ME: '/api/guardians/me',
    EMAIL_REQUEST: '/api/guardians/me/email/request',
    EMAIL_VERIFY: '/api/guardians/me/email/verify',
  },
  // Students (routed to student-service via /api/students)
  STUDENTS: {
    LIST: '/api/students/',
    CREATE: '/api/students/',
    GET: (id: string) => `/api/students/${id}`,
    UPDATE: (id: string) => `/api/students/${id}`,
    DELETE: (id: string) => `/api/students/${id}`,
    REFRESH_TOKEN: (id: string) => `/api/students/${id}/refresh-token`,
    BY_REGISTRY_ID: (registryId: string) => `/api/students/by-registry-id/${registryId}`,
  },
  // Vaccinations (routed to immunization-service via /api/vaccinations)
  VACCINATIONS: {
    UPLOAD: (studentId: string) => `/api/vaccinations/${studentId}/vaccinations/upload`,
    // Asynchronous extraction: 202 + job id, then poll JOBS. A dense card
    // takes 50-75 s; held open as one request it was dropped by mobile
    // clients / NAT at ~46 s (docs/IMMUNIZATION_UPLOAD_CONNECTION_LOST_RCA_AND_ASYNC_SPEC.md).
    UPLOAD_ASYNC: (studentId: string) => `/api/vaccinations/${studentId}/vaccinations/upload-async`,
    JOBS: (studentId: string, jobId: string) => `/api/vaccinations/${studentId}/jobs/${jobId}`,
    // Webcam capture posts JSON base64 rather than multipart.
    UPLOAD_BASE64: (studentId: string) =>
      `/api/vaccinations/${studentId}/vaccinations/upload-base64`,
    GET: (studentId: string) => `/api/vaccinations/${studentId}/vaccinations`,
    ADD: (studentId: string) => `/api/vaccinations/${studentId}/vaccinations`,
    UPDATE: (studentId: string, vaccId: string) => `/api/vaccinations/${studentId}/vaccinations/${vaccId}`,
    DELETE: (studentId: string, vaccId: string) => `/api/vaccinations/${studentId}/vaccinations/${vaccId}`,
    DELETE_ALL: (studentId: string) => `/api/vaccinations/${studentId}/vaccinations`,
    FROM_ANALYSIS: (studentId: string) => `/api/vaccinations/${studentId}/vaccinations/from-analysis`,
    CHECK_QUALITY: '/api/vaccinations/check-quality',
    // Removed: ANALYZE ('/api/vaccinations/analyze') had no backend route and no active callers.
    // Use ANALYSIS.IMAGE ('/api/vaccinations/analyze_image') for document analysis.
  },
  // Compliance (routed to immunization-service via /api/vaccinations/compliance)
  COMPLIANCE: {
    CHECK: (studentId: string) => `/api/vaccinations/${studentId}/compliance/check`,
    GET_STORED: (studentId: string) => `/api/vaccinations/${studentId}/compliance`,
    STATUS: (studentId: string) => `/api/vaccinations/${studentId}/compliance`,
    EXEMPTIONS: (studentId: string) => `/api/vaccinations/${studentId}/compliance/exemptions`,
    REQUIREMENTS: '/api/vaccinations/requirements/florida',
    GENERATE_DH680: (studentId: string) => `/api/vaccinations/${studentId}/dh680`,
    DOWNLOAD_DH680_PDF: (studentId: string) => `/api/vaccinations/${studentId}/dh680/pdf`,
  },
  // Document Analysis (routed to immunization-service via /api/vaccinations)
  ANALYSIS: {
    IMAGE: '/api/vaccinations/analyze_image',
  },
  // Public endpoints (routed to student-service via /api/students)
  PUBLIC: {
    COMPLIANCE: (studentId: string, token: string) => `/api/students/public/${studentId}/compliance?token=${token}`,
  },
  // NOTE: RESOURCES and HELP endpoint groups were removed 2026-05-02.
  // - RESOURCES.CLINICS/EMBASSIES had no functional backend; FreeClinicsLocator
  //   uses hardcoded MOCK_CLINICS and there are no consumers of EMBASSIES.
  // - HELP.CONNECT/DISCONNECT was used only by the orphaned useLiveHelp hook;
  //   the active live-help UI uses useGeminiLive + GeminiLiveModal (direct
  //   Gemini Live API).
  // If these features are revived, restore the constants AND wire up backends
  // in immunization-service / notification-service respectively.
};

export default apiClient;